
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Switch,
  IconButton,
  InputAdornment
} from '@mui/material';
import PaginacionControl from '../components/PaginacionControl';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import SaveAltIcon from '@mui/icons-material/SaveAlt';
import AddIcon from '@mui/icons-material/Add';
import { GridOn } from '@mui/icons-material';
import CloseIcon from '@mui/icons-material/Close';
import SaveIcon from '@mui/icons-material/Save';

export default function TipoDocumento() {
  const [tipos, setTipos] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ codigo_sunat: '', nombre: '', descripcion: '', abreviatura: '', tipo_interno: '', activo: true });
  const [editing, setEditing] = useState(null);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(8);

  useEffect(() => {
    fetchTipos();
  }, []);

  useEffect(() => {
    if (!search) {
      setFiltered(tipos);
    } else {
      setFiltered(
        tipos.filter(tipo =>
          (tipo.codigo || '').toLowerCase().includes(search.toLowerCase()) ||
          (tipo.descripcion || '').toLowerCase().includes(search.toLowerCase()) ||
          (tipo.sunat || '').toLowerCase().includes(search.toLowerCase()) ||
          (tipo.tipo_interno || '').toLowerCase().includes(search.toLowerCase()) ||
          (tipo.abreviatura || '').toLowerCase().includes(search.toLowerCase()) )
      );
    }
    setPage(0);
  }, [search, tipos]);

  const fetchTipos = async () => {
    const res = await axios.get('/api/tipo-documento');
    setTipos(res.data);
    setFiltered(res.data);
  };

  const handleOpen = (tipo = null) => {
    setEditing(tipo);
    // Si viene sunat, mapearlo a codigo_sunat para edición
    if (tipo) {
      setForm({
        ...tipo,
        codigo_sunat: tipo.codigo_sunat || tipo.sunat || ''
      });
    } else {
      setForm({ codigo_sunat: '', nombre: '', descripcion: '', abreviatura: '', tipo_interno: '', activo: true });
    }
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setEditing(null);
  };

  const handleChange = e => {
    const { name, value, type, checked } = e.target;
    setForm({ ...form, [name]: type === 'checkbox' ? checked : value });
  };

  const handleSave = async () => {
    if (editing) {
      await axios.put(`/api/tipo-documento/${editing.id}`, form);
    } else {
      await axios.post('/api/tipo-documento', form);
    }
    fetchTipos();
    handleClose();
  };

  const handleDelete = async id => {
    await axios.delete(`/api/tipo-documento/${id}`);
    fetchTipos();
  };

  const handleExportExcel = async () => {
    const { exportGenericWorkbook } = await import('../utils/excel/genericWorkbook');
    const rows = filtered.map(tipo => ({
      id: tipo.id,
      codigo: tipo.codigo,
      descripcion: tipo.descripcion,
      sunat: tipo.sunat,
      estadoStr: tipo.activo ? 'ACTIVO' : 'INACTIVO'
    }));
    await exportGenericWorkbook({
      titulo: 'REPORTE - TIPOS DE DOCUMENTO',
      sheetName: 'TiposDocumento',
      tableName: 'tblTiposDocumento',
      fileName: 'TiposDocumento',
      columns: [
        { key: 'id', label: 'ID', width: 8 },
        { key: 'codigo', label: 'Código', width: 16 },
        { key: 'descripcion', label: 'Descripción', width: 30 },
        { key: 'sunat', label: 'Cod. SUNAT', width: 18 },
        { key: 'estadoStr', label: 'Estado', width: 14, isEstado: true }
      ],
      rows,
      searchText: search,
      usuario: ''
    });
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 1, mb: 4, width: '100%' }}>
      <Box sx={{ width: { xs: '100%', sm: '95%', md: '90%' }, maxWidth: 1800 }}>
        <Typography variant="h4" gutterBottom sx={{ textAlign: { xs: 'center', md: 'left' }, mb: 3, fontWeight: 'bold', color: '#222' }}>Gestión de Tipos de Documento</Typography>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, mb: 2 }}>
          <TextField
            placeholder="Buscar..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            size="small"
            InputProps={{ startAdornment: <Box sx={{ color: 'text.secondary', mr: 1 }}><SearchIcon /></Box> }}
            sx={{ flex: 1 }}
          />
          <Button
            variant="contained"
            color="success"
            startIcon={<GridOn />}
            onClick={handleExportExcel}
            sx={{ minWidth: 120 }}
          >Exportar Excel</Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpen()} sx={{ ml: { xs: 0, sm: 'auto' }, mt: { xs: 2, sm: 0 } }}>
            Nuevo Tipo
          </Button>
          <TextField
            size="small"
            label="Mostrar"
            type="number"
            value={rowsPerPage}
            onChange={e => { setRowsPerPage(Number(e.target.value)); setPage(0); }}
            sx={{ width: 100 }}
          />
        </Box>
        <TableContainer component={Paper} sx={{ borderRadius: 4, boxShadow: '0 4px 24px rgba(0,0,0,0.08)', mt: 2, overflowX: 'auto' }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell align="center">ID</TableCell>
                <TableCell align="center">Código SUNAT</TableCell>
                <TableCell align="center">Nombre</TableCell>
                <TableCell align="center">Descripción</TableCell>
                <TableCell align="center">Abreviatura</TableCell>
                <TableCell align="center">Tipo Interno</TableCell>
                <TableCell align="center">Estado</TableCell>
                <TableCell align="center">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered
                .slice(rowsPerPage === -1 ? 0 : page * rowsPerPage, rowsPerPage === -1 ? undefined : (page + 1) * rowsPerPage)
                .map(tipo => (
                  <TableRow key={tipo.id}>
                    <TableCell align="center">{tipo.id}</TableCell>
                    <TableCell align="center">{tipo.codigo_sunat || tipo.sunat || tipo.codigo || ''}</TableCell>
                    <TableCell align="center">{tipo.nombre || ''}</TableCell>
                    <TableCell align="center">{tipo.descripcion || ''}</TableCell>
                    <TableCell align="center">{tipo.abreviatura || ''}</TableCell>
                    <TableCell align="center">{tipo.tipo_interno || ''}</TableCell>
                    <TableCell align="center">
                      <Switch checked={tipo.activo} color="success" disabled />
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1 }}>
                        <IconButton color="primary" onClick={() => handleOpen(tipo)} size="small"><EditIcon /></IconButton>
                        <IconButton color="error" onClick={() => handleDelete(tipo.id)} size="small"><DeleteIcon /></IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>
        <PaginacionControl page={page} rowsPerPage={rowsPerPage} count={filtered.length} onPageChange={setPage} />
        <Dialog open={open} onClose={(_, r) => r !== 'backdropClick' && handleClose()} disableEscapeKeyDown={false} maxWidth="sm" fullWidth>
          <DialogTitle>
            <Typography variant="h6" fontWeight="bold">
              {editing ? 'Editar' : 'Nuevo'} Tipo de Documento
            </Typography>
          </DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
              <TextField margin="dense" label="Código SUNAT" name="codigo_sunat" value={form.codigo_sunat} onChange={handleChange} fullWidth required helperText="Ej: 01, 03, 07, 08, CTZ" sx={{ flex: 2 }} />
              <TextField margin="dense" label="Nombre" name="nombre" value={form.nombre} onChange={handleChange} fullWidth required helperText="Ej: Cotización, Factura, Boleta" sx={{ flex: 3 }} />
            </Box>
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
              <TextField margin="dense" label="Descripción" name="descripcion" value={form.descripcion} onChange={handleChange} fullWidth helperText="Ej: Factura Electrónica" sx={{ flex: 7 }} />
              <TextField margin="dense" label="Abreviatura" name="abreviatura" value={form.abreviatura} onChange={handleChange} fullWidth helperText="Ej: COT, FAC, BOL" sx={{ flex: 3 }} />
            </Box>
            <TextField margin="dense" label="Tipo Interno" name="tipo_interno" value={form.tipo_interno} onChange={handleChange} fullWidth helperText="Ej: VENTA, COTIZACION, NOTA" />
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Typography variant="body1" sx={{ mr: 1 }}>Estado:</Typography>
              <Switch checked={form.activo} onChange={e => setForm({ ...form, activo: e.target.checked })} name="activo" color="success" />
              <Typography variant="body2" color={form.activo ? 'success.main' : 'text.secondary'}>
                {form.activo ? 'Activo' : 'Inactivo'}
              </Typography>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose} startIcon={<CloseIcon />} color="error" variant="contained">
              Cancelar
            </Button>
            <Button onClick={handleSave} variant="contained" startIcon={<SaveIcon />} color="primary">
              Guardar
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Box>
  );
}
